import { useCallback, useEffect, useState } from "react";
import { Add, CloseCircle, TickCircle } from "iconsax-reactjs";
import DataTable, { Column } from "../../components/commons/DataTable";
import SkyroomAction from "../../components/partials/skyroom/SkyroomAction";
import SkyRoomModal from "../../components/partials/skyroom/SkyroomModal";
import Button from "../../components/commons/Button";
import type{ SearchSkyroomParams, Skyroom } from "../../services/webServices/skyroom/types";
import { getSkyrooms } from "../../services/webServices/skyroom/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

export default function SkyRoom() {

  const [data, setData] = useState<Skyroom[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchSkyroom = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: SearchSkyroomParams = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    const response = await getSkyrooms({ params });

    setData(response);

    if (response.length < PAGE_SIZE) {
      const calculatedTotal = (currentPage - 1) * PAGE_SIZE + response.length;
      setTotalItems(calculatedTotal);
    } else {
      setTotalItems(currentPage * PAGE_SIZE + 1);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage]);

  useEffect(() => {
    fetchSkyroom();
  }, [fetchSkyroom]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRenderActions = (row: Skyroom) => {
    return (
      <SkyroomAction
        room={row}
        onSuccess={() => fetchSkyroom()}
        onDelete={() => fetchSkyroom()}
      />
    );
  };

  const handleHeaderAction = (
    <Button
      variant="primary"
      style="solid"
      size="md"
      text="ایجاد اتاق جدید"
      loading={isLoading}
      disabled={false}
      onClick={() => setIsCreateModalOpen(true)}
      icon={Add}
    />
  );

  const columns: Column<Skyroom>[] = [
    {
      key: "title",
      header: "نام اتاق",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => row.title,
    },
    {
      key: "max_users",
      header: "حداکثر کاربر",
      align: "right",
      sortable: false,
      width: "15%",
      accessor: (row) => row.max_users,
    },
    {
      key: "guest_login",
      header: "ورود میهمان",
      align: "right",
      sortable: false,
      width: "15%",
      accessor: (row) => (
        <span className="text-sm text-gray-700">
          {row.guest_login ? <TickCircle variant="Outline" color="#00966D" size="20"/> : <CloseCircle variant="Outline" color="#E80202" size="20"/>}
        </span>
      ),
    },
    {
      key: "guest_limit",
      header: "ظرفیت میهمان",
      align: "right",
      sortable: false,
      width: "15%",
      accessor: (row) => row.guest_limit,
    },
    {
      key: "op_login_first",
      header: "ابتدا ورود اپراتور",
      align: "right",
      sortable: false,
      width: "15%",
      accessor: (row) => (
        <span className="text-sm text-gray-700">
          {row.op_login_first ? <TickCircle variant="Outline" color="#00966D" size="20"/> : <CloseCircle variant="Outline" color="#E80202" size="20"/>}
        </span>
      ),
    },
  ];

  return (
    <>
      <SkyRoomModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => fetchSkyroom()}
      />

      <DataTable
        title="لیست اتاق ها"
        data={data}
        columns={columns}
        loading={isLoading}
        loadingText="در حال بارگذاری اتاق ها..."
        emptyText="اتاقی یافت نشد"

        pagination={true}
        serverSidePagination={true}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}

        renderActions={handleRenderActions}
        headerAction={handleHeaderAction}
      />
    </>
  );
};